
import { useState, useEffect } from "react";
import { Dialog } from "@/components/ui/dialog";
import { PricingSheet } from "@/components/pricing/PricingSheet";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Component that shows a pricing dialog when users reach their report limit
 */
export function ReportLimitDialog() {
  const [showDialog, setShowDialog] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Only check limits if we have a user
    if (!user) return;

    const checkReportLimits = async () => {
      try {
        // Check if user is admin first
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('id', user.id)
          .single();
        
        if (profileData?.user_type === 'admin' || user.email === 'kintupercy@gmail.com') {
          console.log('Admin user detected, skipping report limit check');
          return;
        }

        // Get user's report usage
        const { data: reportUsage } = await supabase
          .from('report_usage')
          .select('reports_generated')
          .eq('user_id', user.id)
          .single();
          
        // Get user's profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_type, used_free_report')
          .eq('id', user.id)
          .single();
        
        if (!profile) return;
        
        // Normalize user type (handling any invalid type as free)
        let userType = profile.user_type;
        if (userType !== 'admin' && 
            userType !== 'free' && 
            userType !== 'starter' && 
            userType !== 'pro' && 
            userType !== 'premium') {
          userType = 'free';
        }
        
        const reportCount = reportUsage?.reports_generated || 0;
        
        // Check if user has reached their limit
        let showPricing = false;
        
        if (userType === 'free' && (reportCount >= 2 || profile.used_free_report)) {
          console.log('Free user has reached limit, showing pricing');
          showPricing = true;
        } else if (userType === 'starter' && reportCount >= 10) {
          console.log('Starter user has reached limit, showing pricing');
          showPricing = true;
        } else if (userType === 'pro' && reportCount >= 30) {
          console.log('Pro user has reached limit, showing pricing');
          showPricing = true;
        } else if (userType === 'premium' && reportCount >= 60) {
          console.log('Premium user has reached limit, showing pricing');
          showPricing = true;
        }
        
        setShowDialog(showPricing);
      } catch (error) {
        console.error('Error checking report limits:', error);
      }
    };
    
    // Check limits on component mount and when user changes
    checkReportLimits();
    
    // Also check on window focus (when user returns to the app)
    const handleFocus = () => checkReportLimits();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <PricingSheet onClose={() => setShowDialog(false)} />
    </Dialog>
  );
}
